import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAgencySelector from '@hooks/useAgencySelector'
import useTeam, { useTeamForm } from '@hooks/useTeam'

type ModalCallbacks = {
  closeModal: () => void,
  duplicateTeam: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const DuplicateTeamModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'DuplicateTeamModal', showModal } = props
  const { closeModal, duplicateTeam } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { team } = digObject(modalData, modalKey, {})

  const { selectedAgencyId } = useAgencySelector()

  const teamPayload = useTeam(team, {
    generateUrlsOptions: {
      prefixRoute: `agencies/${selectedAgencyId}/manage`,
    },
  })
  const {
    callbacks: {
      duplicateTeam: duplicateFn,
    },
    creating,
    team: { team_member_count, title: teamTitle },
  } = teamPayload

  const teamForm = useTeamForm({ ...team, title: '' }, { validateOn: 'change' })

  const {
    entityState,
    entityState: { title },
    errors,
    handlers,
  } = teamForm

  const duplicateTeamPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Team Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Duplicate"
        titleSecondLine="Team"
      />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Paragraph boxProps={{ marginTop: 'none' }}>
            You&apos;re duplicating <strong>{teamTitle}</strong> featuring {team_member_count} members.
            Please choose a different name to create your duplicate team.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Form>
          <Form.Field errorMessage={errors.title} label="* New Team Name">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!title}
          loading={creating}
          onClick={() => duplicateTeam(duplicateTeamPayload)}
          size="large"
        >
          Duplicate Team
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicateTeamModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
